import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { BackdropCustom } from '../../../components/backdrop/Backdrop';
import { useOrderStatusService } from '../../../services/useOrderStatusService';
import IOrderStatus from '../../../interfaces/IOrderStatus';
import { useToastr } from '../../../hooks/useToastr';
import IFormError from '../../../interfaces/IFormError';
import { useHubSpotPipelineStageService } from '../../../services/useHubSpotPipelineStageService';
import { useAuth } from '../../../hooks/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import getValidationError from '../../../util/getValidationError';
import IHubSpotPipelineStage from '../../../interfaces/IHubSpotPipelineStage';

interface IHubSpotPipelineStageProps {
  openDialog: boolean;
  hubSpotPipelineId: string;
  hubSpotPipelineStageIdParam: string | null;
  closeDialog: () => void;
}

const HubSpotPipelineStage: React.FC<IHubSpotPipelineStageProps> = ({
  openDialog,
  hubSpotPipelineStageIdParam,
  hubSpotPipelineId,
  closeDialog,
}) => {
  const toastr = useToastr();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const orderStatusService = useOrderStatusService();
  const {
    updateHubSpotPipelineStage,
    createHubSpotPipelineStage,
    deleteHubSpotPipelineStage,
    findHubSpotPipelineStage,
  } = useHubSpotPipelineStageService();

  const [stageId, setstageId] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [orderStatusList, setOrderStatusList] = useState<IOrderStatus[]>([]);
  const [orderStatus, setOrderStatus] = useState<IOrderStatus | null>(null);

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<IFormError>({});

  const clearForm = () => {
    setstageId('');
    setLabel('');
    setOrderStatus(null);
  };

  const listOrderStatus = async () => {
    const url = `perPage=1000&currentPage=1&orderBy=id&orderDirection=ASC`;

    setLoading(true);
    await orderStatusService
      .listOrderStatus(url)
      .then(result => {
        if (result) {
          setOrderStatusList(result.data);
        } else {
          setOrderStatusList([]);
        }
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = useCallback(async (): Promise<void> => {
    setLoading(true);
    setFormErrors({});

    try {
      const data = {
        stageId: stageId || '',
        label: label || '',
        pipelineId: hubSpotPipelineId,
        orderStatusId: orderStatus?.id ? Number(orderStatus.id) : 0,
      };

      const schema = Yup.object().shape({
        stageId: Yup.string().required('ID é obrigatório'),
        label: Yup.string().required('Nome é obrigatório'),
        pipelineId: Yup.string().required('Pipeline é obrigatório'),
        orderStatusId: Yup.number().min(1, 'Status do pedido é obrigatório').required('Status do pedido é obrigatório'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      if (hubSpotPipelineStageIdParam) {
        await updateHubSpotPipelineStage(hubSpotPipelineStageIdParam, data)
          .then(() => {
            toastr.success('Pipeline alterado com sucesso');

            clearForm();
            closeDialog();
          })
          .catch(error => {
            if (error.statusCode === 401) {
              signOut();
              navigate('/');
            }

            toastr.error(error?.message || 'Erro ao alterar configurações da aplicação');
          });
      } else {
        await createHubSpotPipelineStage(data)
          .then(() => {
            toastr.success('Pipeline cadastrada com sucesso');
            clearForm();
            closeDialog();
          })
          .catch(error => {
            if (error.statusCode === 401) {
              signOut();
            }

            toastr.error(error?.message || 'Erro ao cadastrar configurações da aplicação');
          });
      }
    } catch (error: Yup.ValidationError | any) {
      console.log(error);
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }

      toastr.error('Ocorreu um erro ao cadastrar/atualizar o registro');
    } finally {
      setLoading(false);
    }
  }, [
    hubSpotPipelineId,
    label,
    signOut,
    navigate,
    clearForm,
    closeDialog,
    stageId,
    orderStatus,
    toastr,
    updateHubSpotPipelineStage,
    createHubSpotPipelineStage,
  ]);

  const handleDeleteHubSpotPipelineStage = useCallback(async () => {
    if (hubSpotPipelineStageIdParam) {
      setLoading(true);

      await deleteHubSpotPipelineStage(hubSpotPipelineStageIdParam)
        .then(async () => {
          toastr.success('Pipeline deletada com sucesso');
          clearForm();
          closeDialog();
        })
        .catch(error => {
          if (error.statusCode === 401) {
            signOut();
          }

          toastr.error('Erro ao deletar registro');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, hubSpotPipelineStageIdParam]);

  const handleListHubSpotPipelineStage = useCallback(async () => {
    if (hubSpotPipelineStageIdParam) {
      setLoading(true);
      await findHubSpotPipelineStage(hubSpotPipelineStageIdParam)
        .then(response => {
          const hubSpotPipeline: IHubSpotPipelineStage = response;
          setstageId(hubSpotPipeline.stageId);
          setLabel(hubSpotPipeline.label);
          setOrderStatus(hubSpotPipeline.orderStatus || null);
        })
        .catch(error => {
          if (error.statusCode === 401) {
            signOut();
          }

          toastr.error(error?.message || 'Contate a equipe de suporte');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [signOut, location.pathname, hubSpotPipelineStageIdParam]);

  useEffect(() => {
    listOrderStatus();
  }, []);

  useEffect(() => {
    handleListHubSpotPipelineStage();
  }, [openDialog]);

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        clearForm();
        closeDialog();
      }}
      sx={{ '& .MuiDialog-paper': { width: '70%', maxHeight: 335 } }}
    >
      <DialogTitle sx={{ borderBottom: '#efefef solid 1px' }}>Adicionar estágio do Pipeline</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 2 }}>
          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: '100%' }}
              size="small"
              label="ID"
              value={stageId}
              onChange={e => setstageId(e.target.value)}
              autoComplete="off"
              helperText={formErrors.stageId}
              error={!!formErrors.stageId}
              disabled={!!hubSpotPipelineStageIdParam}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <TextField
              sx={{ width: '100%' }}
              size="small"
              label="Nome"
              value={label}
              onChange={e => setLabel(e.target.value)}
              autoComplete="off"
              helperText={formErrors.label}
              error={!!formErrors.label}
            />
          </Grid>

          <Grid item xs={12} md={6} sm={6}>
            <Autocomplete
              options={orderStatusList}
              fullWidth
              value={orderStatus}
              onChange={(_, newValue) => {
                setOrderStatus(newValue);
              }}
              isOptionEqualToValue={(option, value) => {
                return option?.description === value?.description || true;
              }}
              getOptionLabel={option => (option?.description ? `${option.id} - ${option.description}` : '')}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Status do Pedido"
                  size="small"
                  error={Boolean(formErrors?.orderStatusId)}
                  helperText={formErrors?.orderStatusId}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            clearForm();
            closeDialog();
          }}
          color="inherit"
          sx={{
            textTransform: 'none',
          }}
        >
          Fechar
        </Button>
        {hubSpotPipelineStageIdParam && (
          <Button
            color="error"
            sx={{
              textTransform: 'none',
            }}
            onClick={handleDeleteHubSpotPipelineStage}
          >
            Excluir
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
          }}
        >
          Confirmar
        </Button>
      </DialogActions>
      {loading && <BackdropCustom />}
    </Dialog>
  );
};

export { HubSpotPipelineStage };
